import { Maybe } from "@oysterhr/schema"

export type RequireField<T, K extends keyof T> = T & Required<Pick<T, K>>

export type FlattenObjectKeys<T extends Record<string, unknown>, Key = keyof T> = Key extends string
  ? T[Key] extends Record<string, unknown>
    ? `${Key}.${FlattenObjectKeys<T[Key]>}`
    : `${Key}`
  : never

export function notNullish<TValue>(value: TValue): value is NonNullable<TValue> {
  return value !== null && value !== undefined
}

export function notFalse<TValue>(value: TValue | boolean): value is TValue {
  return value !== false
}

// helper for yup transform function
export const emptyStringToNull = (value: number | null, originalValue: number | string) => {
  if (originalValue === "") {
    return null
  }
  return value
}

// Unwraps array up to one level
// Array<U> -> U
export type Unarray<T> = T extends Array<infer U> ? U : T

export const tuple = <T extends unknown[]>(args: [...T]): T => args

export const assertUnreachable = (x: never): never => {
  console.error(`Didn't expect to get here. Invalid value: ${x}`)
  return x
}

/*
Typescript doesn't support this:
{
  someKnownField: string
  [key: string]: number
}
Because `string` encompases `someKnownField`,
so the definition collapses to { [key: string]: number }.
This prevents me from creating typed sections of the form.
A solution to this would be negated types, like:
{ someKnownField: string, [key: string & not 'someKnownField']: number }
But this is not supported by typescript yet.
So a workaround is to use any, and then cast the form to the desired type.

I researched the react-hook-form github and this seems like a problem without
a solution right now. https://github.com/react-hook-form/react-hook-form/discussions/7354
I'll keep looking and testing, and when I do I'll update this and fix it.
*/

export const typedObjectKeys = <T extends Record<string, unknown>>(obj: T) =>
  Object.keys(obj) as (keyof T)[]

export const typedObjectEntries = <T extends Record<string, unknown>>(obj: T) =>
  Object.entries(obj) as [keyof T, T[keyof T]][]

type UndefinedKeys<T> = {
  [K in keyof T]: undefined extends T[K] ? K : never
}[keyof T]

type NonUndefinedKeys<T> = {
  [K in keyof T]: undefined extends T[K] ? never : K
}[keyof T]

type Primitive = string | number | boolean | bigint | symbol | undefined | null

export type RecursiveUndefinedToOptional<T> = T extends Primitive
  ? T
  : T extends Array<infer R>
    ? Array<RecursiveUndefinedToOptional<R>>
    : T extends object
      ? {
          [K in NonUndefinedKeys<T>]-?: T[K] extends infer U
            ? RecursiveUndefinedToOptional<U>
            : never
        } & { [K in UndefinedKeys<T>]+?: RecursiveUndefinedToOptional<T[K]> }
      : never

// Merge two types recursively, with the second type taking precedence. If possible, merge the types of arrays.
// e.g MergeDeep<{ a: { b: string } }, { a: { c: number } }> = { a: { b: string, c: number } }
// e.g. MergeDeep<{ a: { b: { nestedA: string }[] } }, { a: { b: {nestedB: number}[] } }> = { a: { b: ({nestedA: string} & {nestedB: number})[] } }
// e.g. MergeDeep<{ a: { b: string[] } }, { a: { b: number[] } }> = { a: { b: (string | number)[] } }
export type MergeDeep<T, U> = T extends Primitive
  ? never
  : U extends Primitive
    ? never
    : {
        [P in keyof T | keyof U]: P extends keyof U
          ? U[P] extends Record<string, unknown>
            ? P extends keyof T
              ? MergeDeep<T[P], U[P]>
              : U[P]
            : U[P] extends Array<infer V>
              ? P extends keyof T
                ? T[P] extends Array<infer W>
                  ? Array<MergeDeep<W, V>>
                  : Array<V>
                : Array<V>
              : U[P]
          : P extends keyof T
            ? T[P]
            : never
      }

export type NonNullifier<T> = {
  [P in keyof T]: NonNullable<T[P]>
}

type FunctionReturnRouteHelper<TK> = TK extends undefined ? () => string : (args: TK) => string

/**
 * Useful to type the route helpers exported by a modules' route.app.tsx
 * @example
 * // Create a type like
 * export type ExampleRouteParams = { examplePage: { companyStrongId: string } }
 * // and use like:
 * const exampleRouteHelpers: RouteHelper<ExampleRouteParams> = {
 *       examplePage: ({ companyStrongId }) => `/example/${companyStrongId}/setup`
 * }
 * // companyStrongId in the function above will be automatically typed
 */
export type RouteHelper<T extends Record<string, unknown>> = {
  [K in keyof T]: FunctionReturnRouteHelper<T[K]>
}

/**
 * Allows for quickly building schema like types deriving from the
 * graphql schema. Allows either picking field or merging.
 *
 * The first argument should be the base type
 * The second argument is a union of strings
 * The second thirds is another type to merge
 *
 * @example:
 * type ExampleType = PickAndMerge<Contract, "id" | "name", { engagement: Pick<Engagement, "id" | "salaryAmount"> }>
 *
 * // Results in:
 * // {
 * //   id: string,
 * //   name: string,
 * //   engagement: {
 * //     id: string
 * //     salaryAmount: number
 * //   }
 * // }
 *
 */

export type MergePick<T, U extends keyof T, V> = MergeDeep<Pick<NonNullable<T>, U>, NonNullable<V>>

/**
 * Allows for quickly building schema like types deriving from the
 * graphql schema. Allows either picking field or merging.
 * Same as `PickAndMerge` but makes the object being merged a Maybe<> | undefined
 * This behavior helps a lot when creating subtypes of the schema
 *
 * The first argument should be the base type
 * The second argument is a union of strings
 * The second thirds is another type to merge
 *
 * @example:
 * type ExampleType = {
 *     contract: MaybePickAndMerge<Contract, "id" | "name", { engagement: MaybePick<Engagement, "id" | "salaryAmount"> }>>
 * }
 *
 *
 * // Results in:
 * // {
 * //   contract: Maybe<{
 * //     id: string,
 * //     name: string,
 * //      engagement: Maybe<{
 * //        id: string
 * //        salaryAmount: number
 * //      }> | undefined
 * //    }> | undefined
 *
 */
export type MaybeMergePick<T, U extends keyof T, V> =
  | Maybe<MergePick<NonNullable<T>, U, NonNullable<V>>>
  | undefined

export type MaybePick<T, U extends keyof T> = Maybe<Pick<NonNullable<T>, U>> | undefined

export type MaybeMergeDeep<T, U> = Maybe<MergeDeep<NonNullable<T>, NonNullable<U>>> | undefined
